// Root.jsx

import React, { useEffect, useRef } from 'react';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useStartupStatus } from 'context/StartupStatusContext';
import { useUI } from 'context/UIContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Utilities
import { defaultFont } from 'utilities/Defaults';

// Routing
import { Outlet, useLocation } from 'react-router-dom';

// Components
import Login from 'login/Login';
import SplashScreen from 'components/splash/SplashScreen';

// Custom Hooks
import useConfirmLoginStatus from 'root/hooks/useConfirmLoginStatus';
import useForcedAuthEpochLogout from 'root/hooks/useForcedAuthEpochLogout';
import usePreloadUserEngagements from 'root/hooks/usePreloadUserEngagements';
import usePreloadUserNotifications from 'hooks/usePreloadUserNotifications';
import usePreloadSettings from 'root/hooks/usePreloadSettings';
import usePreloadActiveWorkspace from 'root/hooks/usePreloadActiveWorkspace';

// CSS
import './Root.css';

// Components
import Progress from 'components/progress/Progress';

// Dynamic Styles
import { RootStyles } from './RootStyles';

const Root = () => {

  // Global Context
  const {
    loginOpen,
    setLoginOpen,
  } = useUI();

  const { postAuthRedirect, setPostAuthRedirect } = useNavigation();

  // User Context
  const { user, isAuthLoading } = useUser();
  const { activeWorkspace } = useWorkspace();
  const { hasActiveStatus } = useStartupStatus();

  // Theme Context
  const { theme } = useTheme();

  // Routing
  const location = useLocation();

  // Refs
  const promptedRootLoginRef = useRef(false);

  // HOOKS
  useForcedAuthEpochLogout();
  useConfirmLoginStatus();
  usePreloadUserEngagements();
  usePreloadUserNotifications();
  usePreloadSettings();
  usePreloadActiveWorkspace();

  // When an unauthenticated user lands on a deep link (e.g. /node/:id),
  // remember the target path and prompt for login/signup.
  useEffect(() => {
    if (isAuthLoading) return;
    if (user) return;

    const path = location.pathname + location.search + location.hash;

    // Only capture non-root paths, and avoid clobbering an existing intent.
    if (path && path !== '/' && !postAuthRedirect) {
      setPostAuthRedirect(path);
      setLoginOpen(true);
    }
  }, [isAuthLoading, user, location.pathname, location.search, location.hash, postAuthRedirect, setPostAuthRedirect, setLoginOpen]);

  useEffect(() => {
    if (isAuthLoading) return;
    if (user) return;
    if (location.pathname !== '/') return;
    if (promptedRootLoginRef.current) return;

    promptedRootLoginRef.current = true;
    setLoginOpen(true);
  }, [isAuthLoading, user, location.pathname, setLoginOpen]);

  const shouldShowSplashScreen = isAuthLoading || (Boolean(user) && !activeWorkspace && hasActiveStatus);

  return (
    <>
      <RootStyles
        fontFamily={defaultFont}
        placeholderColor={theme.foregroundMuted}
        scrollbarColor={theme.foregroundMuted}
      />

      {/* LOGIN/SIGNUP */}
      <Login
        isOpen={loginOpen}
        setOpen={setLoginOpen}
      />

      {/* APP */}
      {shouldShowSplashScreen ? (
        <SplashScreen />
      ) : user && (
        <Outlet />
      )}

      {/* PROGRESS PANEL */}
      <Progress />
    </>
  );
};

export default Root;